import { useState } from "react";
import { Upload, ImageIcon, X, Eye, Printer } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { uploadFile, fileUrl } from "@/lib/upload";

export function LogoField({ form, set }) {
  const [prog, setProg] = useState(null);
  const [printing, setPrinting] = useState(false);
  const printSample = async () => {
    setPrinting(true);
    try {
      await api.printerTest(form.printer_ip.trim(), Number(form.printer_port) || 9100, Number(form.printer_width) || 80, form.printer_mode === "network_pdf" ? "pdf" : "escpos",
        { sample: true, contest_name: form.name, logo_file_id: form.logo_file_id || null, logo_url: form.logo_url || null });
      toast.success("Scontrino di prova inviato alla stampante");
    } catch (e) { toast.error(e?.response?.data?.detail || "Stampante non raggiungibile"); } finally { setPrinting(false); }
  };
  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!["image/jpeg", "image/png"].includes(f.type)) return toast.error("Formato non supportato: usa JPEG o PNG");
    setProg(0);
    try {
      const doc = await uploadFile(f, "logo", setProg);
      set("logo_file_id", doc.id);
      toast.success("Logo caricato");
    } catch (err) { toast.error(err.message || "Caricamento non riuscito"); } finally { setProg(null); }
  };
  const src = fileUrl(form.logo_file_id) || form.logo_url;
  return (
    <div className="space-y-2 rounded-xl border border-border p-4" data-testid="contest-logo-section">
      <Label className="flex items-center gap-1.5"><ImageIcon className="h-4 w-4" /> Logo del concorso (JPEG o PNG)</Label>
      <p className="text-xs text-muted-foreground">Compare su ricevute, PDF codici, totem, pagina regolamento ed email.</p>
      <div className="flex items-center gap-3">
        {src ? <img src={src} alt="Logo concorso" className="h-16 w-16 rounded-md bg-white object-contain p-1" data-testid="contest-logo-preview" /> : <div className="h-16 w-16 rounded-md border border-dashed border-border grid place-items-center text-muted-foreground"><ImageIcon className="h-5 w-5" /></div>}
        <label className="flex-1 cursor-pointer">
          <input type="file" accept="image/png,image/jpeg" onChange={pick} className="hidden" data-testid="contest-logo-file-input" />
          <div className="h-10 rounded-md border border-dashed border-border grid place-items-center text-sm text-muted-foreground hover:border-primary">
            {prog !== null ? `Caricamento ${prog}%` : <span className="flex items-center gap-1.5"><Upload className="h-4 w-4" /> {form.logo_file_id ? "Sostituisci file" : "Carica logo"}</span>}
          </div>
        </label>
        {form.logo_file_id && <Button size="icon" variant="ghost" onClick={() => set("logo_file_id", null)} title="Rimuovi" data-testid="contest-logo-remove"><X className="h-4 w-4" /></Button>}
      </div>
      <Input data-testid="contest-logo-input" value={form.logo_url} onChange={(e) => set("logo_url", e.target.value)} placeholder="Oppure URL del logo: https://…/logo.png (facoltativo)" className="text-xs" />
      <div className="flex flex-wrap gap-2 pt-1">
        <a href={api.contestPreviewUrl("receipt", form, "a4")} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-border text-xs hover:bg-secondary/60" data-testid="contest-preview-receipt"><Eye className="h-3.5 w-3.5" /> Anteprima ricevuta A4</a>
        <a href={api.contestPreviewUrl("receipt", form, Number(form.printer_width) === 58 ? "58" : "80")} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-border text-xs hover:bg-secondary/60" data-testid="contest-preview-thermal"><Eye className="h-3.5 w-3.5" /> Anteprima scontrino {Number(form.printer_width) === 58 ? "58" : "80"} mm</a>
        {form.printer_ip?.trim() && ["network", "network_pdf"].includes(form.printer_mode) && (
          <Button type="button" size="sm" variant="secondary" className="h-8 text-xs gap-1.5" disabled={printing} onClick={printSample} data-testid="contest-print-sample">
            <Printer className="h-3.5 w-3.5" /> {printing ? "Invio…" : "Stampa di prova scontrino"}
          </Button>
        )}
        <a href={api.contestPreviewUrl("codes", form)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-border text-xs hover:bg-secondary/60" data-testid="contest-preview-codes"><Eye className="h-3.5 w-3.5" /> Anteprima cartolina codici</a>
      </div>
    </div>
  );
}
